import { MailGenerateAdapter } from '@/common/message-generate/adapters/mail-generate.adapter';
import { MessageGenerateComponent, MessageType } from '@/common/message-generate/message-generate.component';
import { MessageTemplate } from '@/common/message-generate/message-generate.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MailPreviewResponse } from '@/vindication/vindication.model';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	inject,
	NgZone,
	OnInit,
	signal,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { MailPreviewDialogComponent } from './mail-preview-dialog/mail-preview-dialog.component';
import { InvoiceMailAttachmentsDialogComponent } from './invoice-mail-attachments-dialog/invoice-mail-attachments-dialog.component';
import { AttachmentTooltipPipe } from '../../pipes/attachment-tooltip.pipe';
import { filter } from 'rxjs';
import { VindicationApiService } from '@/vindication/vindication.api.service';

const SEND_TOGETHER_DISABLED_TOOLTIP =
	'Wysyłka w jednej wiadomości jest dostępna wyłącznie dla pozycji przypisanych do tego samego kontrahenta.';

@Component({
	selector: 'app-invoice-mail',
	standalone: true,
	imports: [
		MessageGenerateComponent,
		MatTableModule,
		MatTooltipModule,
		MatButtonModule,
		MatBadgeModule,
		AttachmentTooltipPipe,
	],
	template: `<div class="m-3">
			<app-message-generate
				[itemsIds]="invoicesIds()"
				[type]="MessageType.EMAIL"
				[previewTemplate]="mailPreviewTpl"
				[showInOneMailToggle]="true"
				[inOneMailToggleEnabled]="inOneMailToggleEnabled()"
				[inOneMailDisabledTooltip]="sendTogetherDisabledTooltip"
				[attachmentsByItem]="attachmentsByItem()"
				[defaultCc]="defaultCc()"
				(templateChange)="onTemplateChange($event)"
				[adapter]="mailAdapter"
			></app-message-generate>
		</div>

		<ng-template
			#mailPreviewTpl
			let-items
			let-previewLoader="previewLoader"
			let-attachmentsByItem="attachmentsByItem"
		>
			<div class="invoice-mail-preview">
				<div class="invoice-mail-table-scroll">
					<table class="fixed-layout" mat-table [dataSource]="items">
						<ng-container matColumnDef="company">
							<th mat-header-cell *matHeaderCellDef>Firma</th>
							<td mat-cell *matCellDef="let element">
								{{ element.company }}
							</td>
						</ng-container>

						<ng-container matColumnDef="mail">
							<th mat-header-cell *matHeaderCellDef>Mail</th>
							<td mat-cell *matCellDef="let element">
								{{ element.mails }}
							</td>
						</ng-container>

						<ng-container matColumnDef="nip">
							<th mat-header-cell *matHeaderCellDef>NIP</th>
							<td mat-cell *matCellDef="let element">{{ element.nip }}</td>
						</ng-container>

						<ng-container matColumnDef="attachments">
							<th mat-header-cell *matHeaderCellDef>Załączniki</th>
							<td mat-cell *matCellDef="let element">
								<div class="d-flex align-items-center gap-2">
									<button
										mat-icon-button
										class="attachments-badge-button"
										[matTooltip]="
											attachmentsByItem
												| attachmentTooltip: element.invoice : element.files.length
										"
										[matBadge]="
											(attachmentsByItem?.[element.invoice]?.length ?? 0) +
											(element.files.length ?? 0)
										"
										[matBadgeHidden]="
											(attachmentsByItem?.[element.invoice]?.length ?? 0) +
												(element.files.length ?? 0) ===
											0
										"
										matBadgeColor="primary"
										matBadgeSize="small"
										type="button"
										(click)="openAttachmentsDialog(element)"
									>
										<i class="fa fa-paperclip attachment-icon"></i>
									</button>
								</div>
							</td>
						</ng-container>

						<ng-container matColumnDef="subject">
							<th mat-header-cell *matHeaderCellDef>Tytuł</th>
							<td mat-cell *matCellDef="let element">
								{{ element.subject }}
							</td>
						</ng-container>

						<ng-container matColumnDef="message">
							<th mat-header-cell *matHeaderCellDef>Wiadomość</th>
							<td mat-cell *matCellDef="let element">
								<button mat-raised-button color="primary" (click)="openPreviewDialog(element)">
									Podgląd
								</button>
							</td>
						</ng-container>

						<tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
						<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
					</table>
				</div>
			</div>
		</ng-template> `,
	styleUrl: './invoice-mail.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceMailComponent implements OnInit {
	readonly invoicesIds = signal<number[]>([]);

	readonly MessageType = MessageType;

	readonly displayedColumns: string[] = ['company', 'mail', 'nip', 'attachments', 'subject', 'message'];
	readonly sendTogetherDisabledTooltip = SEND_TOGETHER_DISABLED_TOOLTIP;

	readonly attachmentsByItem = signal<Record<number, File[]>>({});
	readonly profileEmail = signal('');
	readonly templateEmail = signal('');
	readonly defaultCc = computed(() => {
		return [this.templateEmail(), this.profileEmail()].filter((email) => !!email).join(', ');
	});
	readonly inOneMailToggleEnabled = signal(false);

	readonly mailAdapter = inject(MailGenerateAdapter);

	private readonly destroyRef = inject(DestroyRef);
	private readonly activateRoute = inject(ActivatedRoute);
	private readonly dialog = inject(MatDialog);
	private readonly ngZone = inject(NgZone);
	private readonly vindicationApiService = inject(VindicationApiService);

	ngOnInit(): void {
		const queryParams = this.activateRoute.snapshot.queryParamMap;
		const ids = queryParams.get('invoices');

		if (ids) {
			this.invoicesIds.set(ids.split(',').map((id) => +id));
			this.inOneMailToggleEnabled.set(queryParams.get('sendTogetherAvailable') === 'true');
		}

		this.vindicationApiService
			.getProfile()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((profile) => {
				const profileEmail = profile.email?.trim();
				this.profileEmail.set(profileEmail ?? '');
			});
	}

	onTemplateChange(template: MessageTemplate | null): void {
		const templateWithLegacyKind = template as MessageTemplate & {
			kind?: { emailFrom?: string; emailForm?: string };
		};
		const emailFrom =
			template?.emailFrom?.trim() ??
			templateWithLegacyKind.kind?.emailFrom?.trim() ??
			templateWithLegacyKind.kind?.emailForm?.trim() ??
			'';
		this.templateEmail.set(emailFrom);
	}

	openPreviewDialog(element: MailPreviewResponse): void {
		this.dialog.open(MailPreviewDialogComponent, {
			data: element,
		});
	}

	async openAttachmentsDialog(element: MailPreviewResponse): Promise<void> {
		const currentAttachments = this.attachmentsByItem()[element.invoice] ?? [];

		const invoiceMailAttachmentsDialogComponent = await import(
			'./invoice-mail-attachments-dialog/invoice-mail-attachments-dialog.component'
		).then((c) => c.InvoiceMailAttachmentsDialogComponent);

		this.ngZone.run(() => {
			this.dialog
				.open<InvoiceMailAttachmentsDialogComponent>(invoiceMailAttachmentsDialogComponent, {
					data: {
						invoice: element.invoice,
						company: element.company,
						nip: element.nip,
						files: element.files,
						attachments: currentAttachments,
					},
					width: '820px',
					maxWidth: '95vw',
					maxHeight: '90vh',
					autoFocus: false,
					disableClose: true,
				})
				.afterClosed()
				.pipe(filter((value): value is File[] => value !== undefined))
				.subscribe((files) => {
					const current = this.attachmentsByItem();
					this.attachmentsByItem.set({ ...current, [element.invoice]: files });
				});
		});
	}
}
